/**
 * EasyCookie Scanner Block
 * Handles domain input, scan functionality, and results display
 * Uses Alpine.js for state management
 * Integrates with iubenda Radar API via plugin
 */

// Constants
const POLLING_INTERVAL = 3000; // 3 seconds

// Alpine.js data function for Easy Cookie Scanner
window.easyCookieScannerData = function () {
  return {
    state: 'form',
    hasError: false,
    errorType: null,
    domain: '',
    scanResults: {
      score: 75,
      privacy_policy: true,
      cookie_policy: true,
      cookie_banner: false,
      terms_conditions: true,
    },
    scanId: null,
    pollingInterval: null,

    // Initialize component - check for scan ID in URL
    init() {
      const urlParams = new URLSearchParams(window.location.search);
      const scanIdFromUrl = urlParams.get('scan');

      if (scanIdFromUrl) {
        this.loadScanFromUrl(scanIdFromUrl);
      }
    },

    // Load scan results from URL parameter
    async loadScanFromUrl(scanId) {
      this.scanId = scanId;
      this.state = 'loading';

      try {
        if (!window.WlgpiEasyCookieScanner?.getScanResults) {
          throw { code: 'missing_api_credentials' };
        }
        const data = await window.WlgpiEasyCookieScanner.getScanResults(scanId);

        if (data.status === 'completed') {
          this.scanResults = data;
          this.state = 'results';
        } else if (data.status === 'pending' || data.status === 'processing') {
          // Scan still in progress, start polling
          this.pollResults();
        } else {
          throw new Error(data.error || 'scan_not_found');
        }
      } catch (error) {
        console.error('Load scan error:', error);
        this.hasError = true;
        this.errorType = error.code || 'scan_not_found';
        this.state = 'form';
        // Remove invalid scan ID from URL
        this.removeUrlParam();
      }
    },

    // Update URL with scan ID
    updateUrlWithScanId(scanId) {
      const url = new URL(window.location.href);
      url.searchParams.set('scan', scanId);
      window.history.replaceState({}, '', url.toString());
    },

    // Remove scan parameter from URL
    removeUrlParam() {
      const url = new URL(window.location.href);
      url.searchParams.delete('scan');
      window.history.replaceState({}, '', url.toString());
    },

    // Initiate scan
    async validateAndScan() {
      // Remove protocol and www if present
      const cleanDomain = this.domain.trim().replace(/^(https?:\/\/)?(www\.)?/, '');

      if (!cleanDomain) {
        this.hasError = true;
        this.errorType = 'invalid_url';
        this.$nextTick(() => {
          const input = this.$root.querySelector('input[type=text]');
          if (input) input.focus();
        });
        return false;
      }

      // Clear any existing polling interval before starting new scan
      this.clearPolling();

      this.hasError = false;
      this.errorType = null;
      this.state = 'loading';

      // Start scan process
      await this.startScan(cleanDomain);

      return true;
    },

    // Start scan via API
    async startScan(domain) {
      try {
        if (!window.WlgpiEasyCookieScanner?.startScan) {
          throw { code: 'missing_api_credentials' };
        }
        const data = await window.WlgpiEasyCookieScanner.startScan(domain);
        this.scanId = data.id;

        // Start polling for results
        this.pollResults();
      } catch (error) {
        console.error('Scan error:', error);

        this.hasError = true;
        this.errorType = error.code || 'scan_failed';
        this.state = 'form';
      }
    },

    // Poll for scan results
    pollResults() {
      this.pollingInterval = setInterval(async () => {
        try {
          if (!window.WlgpiEasyCookieScanner?.getScanResults) {
            throw { code: 'missing_api_credentials' };
          }
          const data = await window.WlgpiEasyCookieScanner.getScanResults(this.scanId);

          if (data.status === 'completed') {
            this.clearPolling();
            this.scanResults = data;
            this.state = 'results';
            // Update URL with scan ID for sharing
            this.updateUrlWithScanId(this.scanId);
          } else if (data.error) {
            throw new Error(data.error);
          }
        } catch (error) {
          console.error('Polling error:', error);
          this.clearPolling();
          this.hasError = true;
          this.errorType = error.code || 'scan_failed';
          this.state = 'form';
        }
      }, POLLING_INTERVAL);
    },

    // Clear polling interval
    clearPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    // Get compliance score (calculated on server)
    getScore() {
      return this.scanResults?.score || 0;
    },

    // Check if specific compliance item was found
    isFound(key) {
      // For mock data / styling, check directly on scanResults
      if (this.scanResults && typeof this.scanResults[key] !== 'undefined') {
        return this.scanResults[key];
      }
      // For real API data, use the plugin helper
      if (!window.WlgpiEasyCookieScanner?.isItemFound) {
        return false;
      }
      return window.WlgpiEasyCookieScanner.isItemFound(this.scanResults, key);
    },

    // Cleanup when component is destroyed
    destroy() {
      this.clearPolling();
    },
  };
};
